import React, { useState } from 'react';
import { CheckIcon, Share2Icon } from 'lucide-react';
import { CommunityGlyph } from '../icons/BeelyvIcons';
import { Button } from '../ui/Button';
import { useLocale } from '../../contexts/LocaleContext';
import { useEngagement } from '../../contexts/EngagementContext';
import type { CommunityChallenge } from '../../types/engagement';

/** One optional weekly community challenge. Progress is private until shared. */
export function ChallengeCard({
  challenge
}: {
  challenge: CommunityChallenge;
}) {
  const {
    t,
    tl,
    locale
  } = useLocale();
  const engagement = useEngagement();
  const [shared, setShared] = useState(false);
  const joined = engagement.joinedChallenges.includes(challenge.id);
  const ratio = Math.min(1, challenge.progress / challenge.target);
  const done = joined && challenge.progress >= challenge.target;
  return <article className="rounded-card border border-sand-line bg-white/70 p-2" data-id="element-577" data-mp="3fv9zvlrp82y6">
      <div className="flex items-start justify-between gap-2" data-id="element-578" data-mp="3ks9y5aib41eq">
        <div className="min-w-0" data-id="element-579" data-mp="zn2jj247z8d6">
          <h3 className="text-[14px] font-bold text-deep" data-id="element-580" data-mp="zm97blmhrqnn">{tl(challenge.title)}</h3>
          <p className="mt-[2px] text-[12px] leading-snug text-muted" data-id="element-581" data-mp="unfuzwixy5p2">{tl(challenge.description)}</p>
        </div>
        {done && <span className="bee-overline shrink-0 rounded-pill bg-gold px-2 py-[4px] text-[8.5px] font-bold text-deep" data-id="element-582" data-mp="18nh41z9d0jfm">
            {t('eng.ch.done')}
          </span>}
      </div>

      {joined && <div className="mt-2" data-id="element-583" data-mp="fxi9xpgb5w43">
          <p className="bee-num text-[12px] font-medium text-deep" data-id="element-584" data-mp="byjc0oszuzm5">
            {t('eng.ch.progress', {
          current: challenge.progress,
          target: challenge.target,
          unit: t(`eng.ch.unit.${challenge.metric}`)
        })}
          </p>
          <div className="mt-1 h-[5px] overflow-hidden rounded-pill bg-sand-deep" role="progressbar" aria-valuemin={0} aria-valuemax={challenge.target} aria-valuenow={challenge.progress} aria-label={tl(challenge.title)} data-id="element-585" data-mp="kwuxkcf0777q">
            <div className={`h-full rounded-pill ${done ? 'bg-gold' : 'bg-soft'}`} style={{
          width: `${ratio * 100}%`
        }} data-id="element-586" data-mp="1xbcyd5bp3ctm" />
          </div>
        </div>}

      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted" data-id="element-587" data-mp="27168e1w6vjvr">
        <span className="bee-num flex items-center gap-1" data-id="element-588" data-mp="19ddxy684em68">
          <CommunityGlyph size={16} aria-hidden="true" data-id="element-589" data-mp="19eyt9y29e955" />
          {t('eng.ch.participants', {
          n: challenge.participants.toLocaleString(locale)
        })}
        </span>
        <span aria-hidden="true" data-id="element-590" data-mp="1jb24dtf39i48">·</span>
        <span className="bee-num" data-id="element-591" data-mp="1ja0jyyc6nj9u">{t('eng.ch.ends', {
          n: challenge.endsInDays
        })}</span>
      </div>

      <div className="mt-2 flex gap-2" data-id="element-592" data-mp="1neqd4ffgijr2">
        <Button variant={joined ? 'outline' : 'primary'} fullWidth aria-pressed={joined} onClick={() => engagement.toggleChallenge(challenge.id)} data-id="element-593" data-mp="2uqqqkikiqd3z">
          {joined && <CheckIcon size={15} className="text-gold-deep" aria-hidden="true" data-id="element-594" data-mp="n4qmzr2a5acc" />}
          {joined ? t('eng.ch.joined') : t('eng.ch.join')}
        </Button>
        {done && <Button variant="quiet" fullWidth onClick={() => setShared(true)} disabled={shared} data-id="element-595" data-mp="j5rddt5lspek">
            <Share2Icon size={15} aria-hidden="true" data-id="element-596" data-mp="eabzx2bicvio" />
            {shared ? t('eng.ch.shared') : t('eng.ch.share')}
          </Button>}
      </div>
    </article>;
}